import { useState } from "react";
import { AutomationWizardStepProps } from "../../../AutomationsUtils";
import { FormProvider, useForm } from "react-hook-form";
import FormWizardFormWrapper from "../../../../Forms/FormWizard/FormWizardFormWrapper";
import PageFormWidget from "../../../../Forms/FormWizard/PageFormRenderer/PageFormWidget/PageFormWidget";
import IntegrationInput from "../../IntegrationInput/IntegrationInput";
import {
  ticketEndpointTypeEnum,
  MappingAlert,
  ticketEndpointColumnsModesEnum,
  useTicketEndpointMutationData,
} from "@SeemplicityDev/ticket-master";
import PageFormButtons from "../../../../Forms/FormWizard/PageFormRenderer/PageFormButtons/PageFormButtons";
import useFormWizard from "../../../../Forms/FormWizard/FormWizardProvider";
import { useUserAnalytics } from "@SeemplicityDev/shared";

const TicketingSystemStep: React.FC<
  AutomationWizardStepProps & {
    fieldMappingId?: string;
  }
> = ({
  defaultValues,
  onValidate = () => {},
  isSummary = false,
  fieldMappingId,
}) => {
  const [disableNextStep, setDisableNextStep] = useState<boolean>(true);
  const [keysFinalPhase, setKeysFinalPhase] = useState<boolean>(false);
  const [keysHasMapping, setKeysHasMapping] = useState<boolean>(false);

  const { trackEvent } = useUserAnalytics();

  const methods = useForm({
    mode: "all",
    values: defaultValues,
  });

  const { handleSubmit, getValues, watch } = methods;
  const {
    getPropsFromField: getPropsFromKeyField,
    setGetPropsFromField: setGetPropsFromKeyField,
  } = useTicketEndpointMutationData();

  const { analyticsStepId } = useFormWizard() ?? {}; // in case this is from a form wizard - add the relevant wizard to analytics data
  const wizardAnalyticsProps = analyticsStepId
    ? `, "step": "${analyticsStepId}"`
    : "";

  const selectedIntegrationType = watch("integrationType");

  return (
    <FormProvider {...methods}>
      <FormWizardFormWrapper
        handleSubmit={handleSubmit}
        onSubmit={onValidate}
        getValues={getValues}
      >
        <PageFormWidget isSummary={isSummary}>
          <IntegrationInput
            fieldMappingId={fieldMappingId}
            type={ticketEndpointTypeEnum.TICKET}
            isSummary={isSummary}
            defaultValues={defaultValues}
            onConfigurationChange={() => {
              setKeysFinalPhase(false);
              setKeysHasMapping(false);
              setDisableNextStep(true);
            }}
            onFinalPhase={(isFinal, hasMapping) => {
              setDisableNextStep(!isFinal);
              setKeysFinalPhase(isFinal as boolean);
              setKeysHasMapping(hasMapping as boolean);
            }}
            onGetKeysProps={setGetPropsFromKeyField}
          />
          {keysFinalPhase && !isSummary && (
            <MappingAlert
              formMethods={methods}
              wizardAnalyticsProps={wizardAnalyticsProps}
              mode={
                keysHasMapping
                  ? ticketEndpointColumnsModesEnum.EDIT
                  : ticketEndpointColumnsModesEnum.CREATE
              }
              getPropsFromField={getPropsFromKeyField}
              onAlertClick={(mode: typeof ticketEndpointColumnsModesEnum) => {
                if (mode === ticketEndpointColumnsModesEnum.EDIT) {
                  trackEvent("Q_Edit_Mapping");
                } else if (mode === ticketEndpointColumnsModesEnum.CREATE) {
                  trackEvent("Q_Create_Mapping");
                }
              }}
              selectedIntegrationType={selectedIntegrationType}
            />
          )}
        </PageFormWidget>
        {!isSummary && (
          <PageFormButtons
            data-testid="ticketing-system-step-buttons"
            nextButtonDisabled={disableNextStep}
          />
        )}
      </FormWizardFormWrapper>
    </FormProvider>
  );
};

export default TicketingSystemStep;
